import React, { useEffect, useMemo, useState } from 'react';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import { User, ClassificationLevel } from '@core/generated/gql-types';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { userActions } from '@core/user/user.action';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { useTenantId } from '@/contexts/TenantContext';
import EditableSection from '@/components/shared/EditableSection';
import { actionChipClassName } from '@core/theme/actionTheme.util';
import ClassificationCardItem from './ClassificationCardItem';
import ClassificationLevelDeleteModal from './ClassificationLevelDeleteModal';
import ClassificationLevelFormModal from './ClassificationLevelFormModal';
import {
  classificationIdsChanged,
  uniqueClassificationIds,
} from '../../../../utils/classificationLevels.utils';
import { ClassificationCard, emptyModalState, ModalState } from './types';

interface ClassificationLevelsPanelProps {
  worker: User;
}

const ClassificationLevelsPanel: React.FC<ClassificationLevelsPanelProps> = ({
  worker,
}) => {
  const tenantId = useTenantId();
  const {
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
    fetchClassificationGroups,
    isLoading,
  } = useClassificationStore();
  const { fetchDetailedWorkerById, updateWorker } = useWorkerStore();
  const { getTerm } = useTemplateStore();

  const [isPersisting, setIsPersisting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [cardPendingDelete, setCardPendingDelete] =
    useState<ClassificationCard | null>(null);
  const [modalState, setModalState] = useState<ModalState>(emptyModalState);

  useEffect(() => {
    if (tenantId) {
      fetchClassificationGroups(tenantId);
    }
  }, [tenantId, fetchClassificationGroups]);

  const cards = useMemo(() => {
    if (classificationGroups.length === 0) return [];

    const resolvedCards: ClassificationCard[] = [];
    const classificationIdsWithLevels = new Set<string>();

    worker.classificationLevelIds?.forEach((levelId) => {
      const level = getClassificationLevelById(levelId);
      if (!level) return;

      const classification = getClassificationById(level.classificationId!);
      const group = classificationGroups.find((classificationGroup) =>
        classificationGroup.classifications?.some(
          (item) => item?.id === level.classificationId,
        ),
      );

      if (group && classification) {
        resolvedCards.push({
          id: level.id!,
          classificationGroup: group,
          classification,
          classificationLevel: level,
        });
        classificationIdsWithLevels.add(level.classificationId!);
      }
    });

    worker.classificationIds?.forEach((classificationId) => {
      if (classificationIdsWithLevels.has(classificationId)) return;

      const classification = classificationGroups
        .flatMap((group) => group.classifications)
        .find((item) => item?.id === classificationId);

      const group = classificationGroups.find((classificationGroup) =>
        classificationGroup.classifications?.some(
          (item) => item?.id === classificationId,
        ),
      );

      if (group && classification) {
        resolvedCards.push({
          id: `unset-${classificationId}`,
          classificationGroup: group,
          classification,
        });
      }
    });

    return resolvedCards;
  }, [
    worker.classificationLevelIds,
    worker.classificationIds,
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
  ]);

  const assignedLevelIds = useMemo(
    () => new Set(worker.classificationLevelIds ?? []),
    [worker.classificationLevelIds],
  );

  const sortedClassificationGroups = [...classificationGroups].sort(
    (groupA, groupB) => (groupA?.name ?? '').localeCompare(groupB?.name ?? ''),
  );

  const availableClassifications = [
    ...(modalState.classificationGroup?.classifications?.filter(Boolean) || []),
  ].sort((classificationA, classificationB) =>
    (classificationA?.name ?? '').localeCompare(classificationB?.name ?? ''),
  );

  const availableLevels =
    modalState.classification?.classificationLevels?.filter(Boolean) || [];

  const persistAndRefresh = async (
    work: () => Promise<void>,
  ): Promise<boolean> => {
    setIsPersisting(true);
    try {
      await work();
      await fetchDetailedWorkerById(worker.id);
      toast.success(
        `${getTerm(TemplateTerm.WORKER, false)} updated successfully`,
      );
      return true;
    } catch (error) {
      console.error('Failed to save classification levels:', error);
      toast.error('Failed to save classification levels');
      return false;
    } finally {
      setIsPersisting(false);
    }
  };

  const openAddModal = () => {
    setEditingCardId(null);
    setModalState(emptyModalState);
    setIsModalOpen(true);
  };

  const openEditModal = (card: ClassificationCard) => {
    setEditingCardId(card.id);
    setModalState({
      classificationGroup: card.classificationGroup,
      classification: card.classification,
      classificationLevel: card.classificationLevel,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isPersisting) return;
    setIsModalOpen(false);
    setEditingCardId(null);
    setModalState(emptyModalState);
  };

  const handleAssignLevel = async (
    card: ClassificationCard,
    level: ClassificationLevel,
  ) => {
    if (!level.id || isPersisting) return;

    if (assignedLevelIds.has(level.id)) {
      toast.error('This classification level is already assigned');
      return;
    }

    await persistAndRefresh(async () => {
      await userActions.assignClassificationLevelToUser(worker.id, level.id!);
    });
  };

  const handleModalConfirm = async () => {
    if (
      !modalState.classificationGroup ||
      !modalState.classification ||
      !modalState.classificationLevel?.id
    ) {
      toast.error('Please select all fields');
      return;
    }

    const newLevelId = modalState.classificationLevel.id;
    const editingCard = cards.find((card) => card.id === editingCardId);

    if (editingCard?.classificationLevel?.id === newLevelId) {
      closeModal();
      return;
    }

    if (assignedLevelIds.has(newLevelId)) {
      toast.error('This classification level is already assigned');
      return;
    }

    const currentClassificationIds = worker.classificationIds ?? [];
    const nextClassificationIds = uniqueClassificationIds([
      ...currentClassificationIds.filter(
        (classificationId) =>
          !editingCard || classificationId !== editingCard.classification.id,
      ),
      modalState.classification.id,
    ]);

    const didSave = await persistAndRefresh(async () => {
      if (editingCard?.classificationLevel?.id) {
        await userActions.removeClassificationLevelFromUser(
          worker.id,
          editingCard.classificationLevel.id,
        );
      }

      await userActions.assignClassificationLevelToUser(worker.id, newLevelId);

      if (
        classificationIdsChanged(
          currentClassificationIds,
          nextClassificationIds,
        )
      ) {
        await updateWorker(worker.id, {
          classificationIds: nextClassificationIds,
        });
      }
    });

    if (didSave) {
      setIsModalOpen(false);
      setEditingCardId(null);
      setModalState(emptyModalState);
    }
  };

  const handleConfirmDelete = async () => {
    if (!cardPendingDelete) return;

    const currentClassificationIds = worker.classificationIds ?? [];
    const nextClassificationIds = currentClassificationIds.filter(
      (classificationId) =>
        classificationId !== cardPendingDelete.classification.id,
    );

    const didSave = await persistAndRefresh(async () => {
      if (cardPendingDelete.classificationLevel?.id) {
        await userActions.removeClassificationLevelFromUser(
          worker.id,
          cardPendingDelete.classificationLevel.id,
        );
      }

      if (
        classificationIdsChanged(
          currentClassificationIds,
          nextClassificationIds,
        )
      ) {
        await updateWorker(worker.id, {
          classificationIds: nextClassificationIds,
        });
      }
    });

    if (didSave) {
      setCardPendingDelete(null);
    }
  };

  return (
    <EditableSection title="Classification Levels" collapsible>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        {cards.map((card) => (
          <ClassificationCardItem
            key={card.id}
            card={card}
            isPersisting={isPersisting}
            onAssignLevel={handleAssignLevel}
            onEdit={openEditModal}
            onDelete={setCardPendingDelete}
          />
        ))}
        <button
          type="button"
          onClick={openAddModal}
          disabled={isPersisting}
          className={`flex min-h-[88px] items-center justify-center gap-2 rounded-lg px-3 py-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionChipClassName}`}
        >
          <PlusCircleIcon className="h-6 w-6" />
          Add Classification Level
        </button>
      </div>

      <ClassificationLevelFormModal
        isOpen={isModalOpen}
        isPersisting={isPersisting}
        isLoadingGroups={isLoading}
        isEditing={Boolean(editingCardId)}
        modalState={modalState}
        sortedClassificationGroups={sortedClassificationGroups}
        availableClassifications={availableClassifications}
        availableLevels={availableLevels}
        onClose={closeModal}
        onGroupChange={(group) =>
          setModalState({
            classificationGroup: group,
            classification: undefined,
            classificationLevel: undefined,
          })
        }
        onClassificationChange={(classification) =>
          setModalState({
            ...modalState,
            classification,
            classificationLevel: undefined,
          })
        }
        onLevelChange={(level) =>
          setModalState({
            ...modalState,
            classificationLevel: level,
          })
        }
        onConfirm={handleModalConfirm}
      />

      <ClassificationLevelDeleteModal
        card={cardPendingDelete}
        isPersisting={isPersisting}
        onClose={() => {
          if (!isPersisting) setCardPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </EditableSection>
  );
};

export default ClassificationLevelsPanel;
